<script setup>
import { reactive, ref, onMounted } from "vue"
import Swal from "sweetalert2"
import {
  parametros,
  PARAMETROS_DEFAULT,
  cargarParametros,
  guardarParametros,
  restaurarParametros,
  parametrosActualizadosEn
} from "@/utils/parametrosManual"

// Copia editable: solo se aplica al presionar "Guardar cambios".
const form = reactive(JSON.parse(JSON.stringify(parametros)))
const cargando = ref(true)
const guardando = ref(false)

onMounted(async () => {
  await cargarParametros(true) // trae lo último guardado en la BD
  Object.assign(form, JSON.parse(JSON.stringify(parametros)))
  cargando.value = false
})

// ---------------------------------------------------------------------
// Definición declarativa de las secciones y campos del formulario.
// key = ruta dentro de `parametros` (grupo.campo).
// ---------------------------------------------------------------------
const secciones = [
  {
    grupo: "general",
    titulo: "Generales",
    icono: "ni ni-settings-gear-65",
    descripcion: "Aplican a todas las plantillas del cotizador manual.",
    campos: [
      { key: "fee", label: "Fee vuelos / servicios", ayuda: "1.15 = recargo del 15% sobre vuelos, asistencia, tours y traslados" },
      { key: "redondeo", label: "Redondeo final (COP)", ayuda: "MROUND del precio de venta. Ej: 1000 redondea a miles" }
    ]
  },
  {
    grupo: "internacional",
    titulo: "TC Internacional",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" }
    ]
  },
  {
    grupo: "nacional",
    titulo: "TC Nacional",
    campos: [
      { key: "gastoCopPaxDia", label: "Gasto COP por persona/día" }
    ]
  },
  {
    grupo: "mex",
    titulo: "TC México (CDMX y combinado con Cancún)",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "trasladosUsdPax", label: "Traslados (USD por persona)" },
      { key: "toursHasta3", label: "Tours USD (1 a 3 personas)" },
      { key: "tours4", label: "Tours USD (4 personas)" },
      { key: "tours5a10", label: "Tours USD (5 a 10 personas)" },
      { key: "tours11a19", label: "Tours USD (11 a 19 personas)" },
      { key: "tours20a30", label: "Tours USD (20 a 30 personas)" },
      { key: "toursUsdPaxDesde31", label: "Tours USD por persona (31 o más)" }
    ]
  },
  {
    grupo: "cancun",
    titulo: "TC Cancún",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "trasladosHasta7", label: "Traslados COP (1 a 7 personas)" },
      { key: "traslados8a14", label: "Traslados COP (8 a 14 personas)" },
      { key: "traslados15a16", label: "Traslados COP (15 a 16 personas)" },
      { key: "trasladosCopPaxDesde17", label: "Traslados COP por persona (17 o más)" }
    ]
  },
  {
    grupo: "playaDelCarmen",
    titulo: "TC Playa del Carmen",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "trasladosHasta7", label: "Traslados COP (1 a 7 personas)" },
      { key: "traslados8a14", label: "Traslados COP (8 a 14 personas)" },
      { key: "traslados15a16", label: "Traslados COP (15 a 16 personas)" },
      { key: "trasladosCopPaxDesde17", label: "Traslados COP por persona (17 o más)" }
    ]
  },
  {
    grupo: "puntaCana",
    titulo: "TC Punta Cana",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "trasladosHasta4", label: "Traslados USD (1 a 4 personas)" },
      { key: "traslados5a6", label: "Traslados USD (5 a 6 personas)" },
      { key: "traslados7a12", label: "Traslados USD (7 a 12 personas)" },
      { key: "trasladosUsdPaxDesde13", label: "Traslados USD por persona (13 o más)" }
    ]
  },
  {
    grupo: "cuba",
    titulo: "TC Cuba",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "trasladosVaraderoUsd", label: "Traslados Varadero (USD por persona)" },
      { key: "trasladosHabanaUsd", label: "Traslados Habana (USD por persona)" },
      { key: "trasladosCombinadoUsd", label: "Traslados Varadero + Habana (USD por persona)" }
    ]
  },
  {
    grupo: "panama",
    titulo: "TC Panamá",
    campos: [
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "ivaTraslados", label: "Factor IVA traslados", ayuda: "1.19 = 19%" },
      { key: "playaSoloUsd", label: "Traslados Playa USD (1 persona)" },
      { key: "playaGrupoUsd", label: "Traslados Playa USD por persona (2 o más)" },
      { key: "ciudadSoloUsd", label: "Traslados Ciudad USD (1 persona)" },
      { key: "ciudadGrupoUsd", label: "Traslados Ciudad USD por persona (2 o más)" },
      { key: "combinadoSoloUsd", label: "Traslados Combinado USD (1 persona)" },
      { key: "combinadoGrupoUsd", label: "Traslados Combinado USD por persona (2 o más)" }
    ]
  },
  {
    grupo: "otros",
    titulo: "Otros destinos (USD)",
    campos: [
      { key: "feeHotel", label: "Fee hotel", ayuda: "1.06 = recargo del 6% sobre el neto USD" },
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" }
    ]
  },
  {
    grupo: "turquia",
    titulo: "Turquía",
    campos: [
      { key: "tarifaParejaUsd", label: "Tarifa terrestre USD por pareja" },
      { key: "factorTrm", label: "Factor sobre TRM", ayuda: "0.85 = se cotiza al 85% de la TRM" },
      { key: "asistenciaUsdDia", label: "Asistencia (USD por persona/día)" },
      { key: "margen", label: "Margen de venta", ayuda: "1.2 = 20% sobre el costo" },
      { key: "ivaNochesAdicionales", label: "Divisor noches adicionales", ayuda: "Neto de noches adicionales / 1.18" },
      { key: "diasAntesSalida", label: "Días de vuelo antes del inicio del plan" },
      { key: "diasPlan", label: "Días entre ida y regreso" }
    ]
  },
  {
    grupo: "tailandia",
    titulo: "Tailandia",
    campos: [
      { key: "tarifaParejaUsd", label: "Tarifa terrestre USD por pareja" },
      { key: "tarifaSencillaUsd", label: "Tarifa terrestre USD sencilla" },
      { key: "tarifaNinoUsd", label: "Tarifa terrestre USD niño" },
      { key: "factorTrm", label: "Factor sobre TRM" },
      { key: "margen", label: "Margen de venta" },
      { key: "ivaNochesAdicionales", label: "Divisor noches adicionales" },
      { key: "diasAntesSalida", label: "Días de vuelo antes del inicio del plan" },
      { key: "diaVueloInterno", label: "Día del vuelo interno (desde la ida)" },
      { key: "diaRegreso", label: "Día del regreso (desde la ida)" }
    ]
  },
  {
    grupo: "sanAndres",
    titulo: "San Andrés",
    campos: [
      { key: "factorComision", label: "Factor comisión tarifario", ayuda: "0.87 = descuento del 13% sobre el tarifario" },
      { key: "iva", label: "Factor IVA", ayuda: "1.18 = 18%" }
    ]
  },
  {
    grupo: "costa",
    titulo: "Santa Marta / Cartagena",
    campos: [
      { key: "ivaSoloDesayuno", label: "Factor solo desayuno", ayuda: "1.19 sobre el neto Travel Compositor" },
      { key: "ivaAlimentacion", label: "Factor plan con alimentación", ayuda: "1.18 sobre tarifa × personas × noches" },
      { key: "descuentoBase", label: "Descuento base comisionable", ayuda: "0.14 = 14% (tarifa total del tarifario)" }
    ]
  },
  {
    grupo: "gruposTravel",
    titulo: "Grupos Travel",
    campos: [
      { key: "factorHotel", label: "Factor sobre neto hotel", ayuda: "1.18 = 18%" }
    ]
  },
  {
    grupo: "crucero",
    titulo: "Crucero desde Cartagena",
    campos: [
      { key: "noches", label: "Noches del crucero" },
      { key: "fechaCambioNaviera", label: "Fecha cambio de naviera", tipo: "date" },
      { key: "navieraAntes", label: "Naviera antes de esa fecha", tipo: "text" },
      { key: "navieraDespues", label: "Naviera desde esa fecha", tipo: "text" }
    ]
  }
]

async function guardar() {
  guardando.value = true
  try {
    await guardarParametros(JSON.parse(JSON.stringify(form)))
    Swal.fire({
      icon: "success",
      title: "Parámetros guardados",
      text: "Se aplicaron para todos los usuarios del cotizador manual.",
      timer: 2200,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}

async function restaurar() {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Restaurar valores de fábrica?",
    text: "Se perderán todos los cambios guardados y aplicará para todos los usuarios.",
    showCancelButton: true,
    confirmButtonText: "Sí, restaurar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  })
  if (!isConfirmed) return

  guardando.value = true
  try {
    await restaurarParametros()
    Object.assign(form, JSON.parse(JSON.stringify(PARAMETROS_DEFAULT)))
    Swal.fire({
      icon: "success",
      title: "Valores restaurados",
      timer: 1800,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo restaurar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}
</script>

<template>
  <div class="container-fluid py-4">

    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="mb-1">
            <i class="ni ni-settings-gear-65 text-primary me-2"></i>
            Parámetros del Cotizador Manual
          </h5>
          <p class="text-sm text-muted mb-0">
            Valores usados por las fórmulas de la vista Cotización Manual.
            Se guardan en la base de datos y aplican para <strong>todos los usuarios</strong>.
          </p>
          <p v-if="parametrosActualizadosEn" class="text-xs text-muted mb-0">
            Última actualización: {{ new Date(parametrosActualizadosEn).toLocaleString("es-CO") }}
          </p>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-danger btn-sm mb-0"
            :disabled="cargando || guardando" @click="restaurar">
            <i class="ni ni-curved-next me-1"></i> Restaurar fábrica
          </button>
          <button type="button" class="btn btn-primary btn-sm mb-0"
            :disabled="cargando || guardando" @click="guardar">
            <i class="ni ni-check-bold me-1"></i>
            {{ guardando ? "Guardando..." : "Guardar cambios" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="cargando" class="text-center text-muted py-5">
      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
      Cargando parámetros...
    </div>

    <div v-else class="row">
      <div v-for="s in secciones" :key="s.grupo" class="col-12 col-xl-6 mb-4">
        <div class="card h-100">
          <div class="card-header pb-2">
            <h6 class="mb-0">{{ s.titulo }}</h6>
            <p v-if="s.descripcion" class="text-xs text-muted mb-0">{{ s.descripcion }}</p>
          </div>
          <div class="card-body pt-2">
            <div class="row g-3">
              <div v-for="c in s.campos" :key="c.key" class="col-md-6">
                <label class="form-label text-sm mb-1" :for="`${s.grupo}-${c.key}`">
                  {{ c.label }}
                </label>
                <input
                  :id="`${s.grupo}-${c.key}`"
                  v-if="(c.tipo ?? 'number') === 'number'"
                  type="number"
                  step="any"
                  class="form-control form-control-sm"
                  v-model.number="form[s.grupo][c.key]"
                >
                <input
                  v-else
                  :id="`${s.grupo}-${c.key}`"
                  :type="c.tipo"
                  class="form-control form-control-sm"
                  v-model="form[s.grupo][c.key]"
                >
                <small v-if="c.ayuda" class="text-muted text-xs">{{ c.ayuda }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!cargando" class="d-flex justify-content-end gap-2 mb-4">
      <button type="button" class="btn btn-outline-danger mb-0"
        :disabled="guardando" @click="restaurar">
        Restaurar fábrica
      </button>
      <button type="button" class="btn btn-primary mb-0"
        :disabled="guardando" @click="guardar">
        {{ guardando ? "Guardando..." : "Guardar cambios" }}
      </button>
    </div>

  </div>
</template>
